import React, { useState, useCallback, useRef, useEffect } from "react"
import { Handle, Position, useNodes, useEdges } from "reactflow"
import "./Node.css"
import "./IfElseNode.css"
import { 
  GitBranch, 
  Minus,
  Plus,
  MoreHorizontal, 
  Trash2,
  Plus as PlusIcon,
  XCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  AlertTriangle
} from "lucide-react"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import VariableReference from "./VariableReference"
import CopyPasteButtons from "./CopyPasteButtons"
import { router } from "@inertiajs/react"
import {
  collectUpstreamFormFieldVariables,
  formatFormFieldSelectOptionLabel,
  resolveWorkflowGraphForVariableDiscovery,
} from "@/lib/workflowFormFieldVariables"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const CONDITION_LABEL_MAX_LENGTH = 50

const LABEL_PARAM_KEYS = [
  "field",
  "operator",
  "value",
  "comparisonType",
  "responseBodyKey",
  "expression",
]

const ensureString = (value) => (value === null || value === undefined ? "" : String(value))

const isAnyDefaultConditionName = (description) => {
  const trimmed = (description || "").trim()
  return trimmed === "" || /^Condition \d+$/i.test(trimmed) || /^#\d+$/.test(trimmed)
}

const truncateLabel = (text, max = CONDITION_LABEL_MAX_LENGTH) => {
  const value = String(text || "")
  if (value.length <= max) return value
  return `${value.slice(0, max - 1)}…`
}

const processConditions = (conditions) => {
  if (!Array.isArray(conditions)) return []

  return conditions.map((condition, index) => {
    let conditionId = condition.id
    if (!conditionId) {
      if (index === 0) {
        conditionId = "1"
      } else {
        conditionId = `condition-${Date.now()}-${index}`
      }
    }

    return {
      id: conditionId,
      type: condition.type || (index === 0 ? "if" : "else if"),
      description: condition.nameCustomized
        ? ensureString(condition.description)
        : (ensureString(condition.description) || `Condition ${index + 1}`),
      nameCustomized: condition.nameCustomized === true,
      operator: condition.operator || "equals",
      value: ensureString(condition.value) || "",
      field: ensureString(condition.field) || "status",
      comparisonType: condition.comparisonType || "response_field",
      expression: ensureString(condition.expression) || "",
      responseBodyKey: ensureString(condition.responseBodyKey) || "",
    }
  })
}

const buildNodePersistPayload = (source, { expanded = true, data = {} } = {}) => ({
  name: source?.name === null || source?.name === undefined ? "If/Else" : ensureString(source.name),
  description: ensureString(source?.description),
  conditions: processConditions(source?.conditions),
  expanded,
  autoLayoutMode: source?.autoLayoutMode ?? data.autoLayoutMode ?? false,
  handlePosition: source?.handlePosition ?? data.handlePosition ?? null,
  dynamicDateFormat: source?.dynamicDateFormat ?? data.dynamicDateFormat ?? "iso",
})

function ConditionNameLabel({ title }) {
  const wrapperRef = useRef(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const checkTruncation = useCallback(() => {
    const titleEl = wrapperRef.current?.querySelector(".condition-title")
    if (!titleEl) return
    setIsTruncated(titleEl.scrollWidth > titleEl.clientWidth + 1)
  }, [])

  useEffect(() => {
    checkTruncation()
  }, [title, checkTruncation])

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper || typeof ResizeObserver === "undefined") return undefined

    const observer = new ResizeObserver(() => checkTruncation())
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [checkTruncation])

  const titleClassName = "condition-title dark:text-white"

  if (!isTruncated) {
    return (
      <div ref={wrapperRef} className="condition-title-wrapper">
        <span className={titleClassName}>{title}</span>
      </div>
    )
  }

  return (
    <div ref={wrapperRef} className="condition-title-wrapper">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={titleClassName}>{title}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm break-words text-left">
          {title}
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

const IfElseNode = ({ data, id, csrf_token }) => {
  const { currentWorkflow, workflows, deleteNode, updateNode } = useWorkflow()
  const resolvedWorkflowId = currentWorkflow?.id ?? data?.workflowId ?? null
  const stepDatabaseId =
    data?.databaseId ??
    (typeof id === "string" && id.startsWith("step-") ? id.replace("step-", "") : null)
  
  // Prefer live React Flow graph state (reflects immediate node data updates),
  // then fall back to workflow context snapshots.
  const liveNodes = useNodes()
  const liveEdges = useEdges()
  const currentWorkflowData = workflows.find(w => w.id === currentWorkflow?.id)
  const { nodes, edges } = resolveWorkflowGraphForVariableDiscovery(
    liveNodes,
    liveEdges,
    currentWorkflowData?.nodes || [],
    currentWorkflowData?.edges || [],
  )
  
  const { expanded = true } = data
  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const [activeTab, setActiveTab] = useState("conditions")
  const [expandedConditions, setExpandedConditions] = useState(new Set()) // Track which conditions are expanded
  const conditionRefs = useRef({})
  const nodeContentRef = useRef(null)
  const isRequestLikeNode = (node) => node?.type === "requestNode" || node?.type === "getNoteNode"

  // Condition data state
  const [conditionData, setConditionData] = useState(() => ({
    name: data.name === null || data.name === undefined ? "If/Else" : ensureString(data.name),
    description: ensureString(data.description),
    conditions: processConditions(data.conditions),
  }))

  const conditionDataRef = useRef(conditionData)

  useEffect(() => {
    const nextConditionData = {
      name: data.name === null || data.name === undefined ? "If/Else" : ensureString(data.name),
      description: ensureString(data.description),
      conditions: processConditions(data.conditions),
    }
    conditionDataRef.current = nextConditionData
    setConditionData(nextConditionData)
  }, [id])

  useEffect(() => {
    conditionDataRef.current = conditionData
  }, [conditionData])

  // Get available variables from all connected request nodes throughout the workflow
  const getAvailableVariables = () => {
    if (!nodes || !edges) return []
    
    const availableVariables = []
    const visitedNodes = new Set()
    
    // Recursive function to find all upstream request nodes
    const findUpstreamRequestNodes = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)
      
      const node = nodes.find(n => n.id === nodeId)
      if (!node) return
      
      // If this is a request node, add its extracted variables
      if (isRequestLikeNode(node) && node.data.extractedVariables) {
        availableVariables.push(...node.data.extractedVariables)
      }
      
      // Find all incoming edges to this node
      const incomingEdges = edges.filter(edge => edge.target === nodeId)
      
      // Recursively check all source nodes
      incomingEdges.forEach(edge => {
        findUpstreamRequestNodes(edge.source)
      })
    }
    
    // Start from the current if/else node
    findUpstreamRequestNodes(id)
    
    return availableVariables
  }

  // Unique JSON keys from upstream Request nodes (from Test / extractedVariables schema)
  const getUpstreamRequestResponseKeys = () => {
    if (!nodes || !edges) return []

    const keySet = new Set()
    const visitedNodes = new Set()

    const visit = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)

      const node = nodes.find((n) => n.id === nodeId)
      if (!node) return

      if (isRequestLikeNode(node)) {
        const extractedVariables = Array.isArray(node.data?.extractedVariables) ? node.data.extractedVariables : []
        const responseData = node.data?.response?.data
        const responseDataKeys =
          responseData && typeof responseData === "object" && !Array.isArray(responseData)
            ? Object.keys(responseData)
            : Array.isArray(responseData) && responseData.length > 0 && responseData[0] && typeof responseData[0] === "object"
              ? Object.keys(responseData[0])
              : []

        extractedVariables.forEach((v) => {
          if (v && v.name != null && String(v.name).trim() !== "") {
            keySet.add(String(v.name))
          }
        })
        responseDataKeys.forEach((k) => {
          if (k != null && String(k).trim() !== "") {
            keySet.add(String(k))
          }
        })
      }

      edges.filter((edge) => edge.target === nodeId).forEach((edge) => visit(edge.source))
    }

    visit(id)
    return Array.from(keySet).sort((a, b) => a.localeCompare(b))
  }

  const getAvailableFormFields = () => collectUpstreamFormFieldVariables(nodes, edges, id)

  // Get available values for a specific field
  const getAvailableValuesForField = (fieldId) => {
    const formFields = getAvailableFormFields()
    const field = formFields.find(f => f.value === fieldId)
    
    if (!field) return []
    
    switch (field.fieldType) {
      case 'select':
      case 'radio':
        // Return the options defined in the form field
        return field.options.map(option => ({
          value: option.value,
          label: option.label
        }))
      
      case 'checkbox':
        // Return boolean values for checkbox
        return [
          { value: 'true', label: 'Checked' },
          { value: 'false', label: 'Unchecked' },
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' }
        ]
      
      case 'number':
        // For number fields, we could provide some common values
        // but mostly users will type their own values
        return [
          { value: '0', label: '0' },
          { value: '1', label: '1' },
          { value: '10', label: '10' },
          { value: '100', label: '100' }
        ]
      
      case 'date':
        // For date fields, provide some common date values
        const today = new Date()
        const tomorrow = new Date(today)
        tomorrow.setDate(tomorrow.getDate() + 1)
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        
        return [
          { value: today.toISOString().split('T')[0], label: 'Today' },
          { value: tomorrow.toISOString().split('T')[0], label: 'Tomorrow' },
          { value: yesterday.toISOString().split('T')[0], label: 'Yesterday' }
        ]
      
      case 'email':
        // For email fields, provide some common email patterns
        return [
          { value: '@gmail.com', label: 'Gmail (@gmail.com)' },
          { value: '@yahoo.com', label: 'Yahoo (@yahoo.com)' },
          { value: '@hotmail.com', label: 'Hotmail (@hotmail.com)' },
          { value: '@outlook.com', label: 'Outlook (@outlook.com)' }
        ]
      
      default:
        // For text, textarea, and other fields, return empty array
        // Users will type their own values
        return []
    }
  }

  // Get operators based on field type
  const getOperatorsForField = (fieldId) => {
    const formFields = getAvailableFormFields()
    const field = formFields.find(f => f.value === fieldId)
    
    if (!field) return operators // Return all operators for non-form fields
    
    switch (field.fieldType) {
      case 'number':
        return [
          { value: "equals", label: "equals" },
          { value: "not_equals", label: "not equals" },
          { value: "greater_than", label: "greater than" },
          { value: "less_than", label: "less than" },
          { value: "greater_than_or_equal", label: "≥" },
          { value: "less_than_or_equal", label: "≤" },
          { value: "is_empty", label: "is empty" },
          { value: "is_not_empty", label: "is not empty" }
        ]
      
      case 'date':
        return [
          { value: "equals", label: "equals" },
          { value: "not_equals", label: "not equals" },
          { value: "greater_than", label: "after" },
          { value: "less_than", label: "before" },
          { value: "greater_than_or_equal", label: "on or after" },
          { value: "less_than_or_equal", label: "on or before" },
          { value: "is_empty", label: "is empty" },
          { value: "is_not_empty", label: "is not empty" }
        ]
      
      case 'checkbox':
        return [
          { value: "equals", label: "equals" },
          { value: "not_equals", label: "not equals" },
          { value: "is_empty", label: "is empty" },
          { value: "is_not_empty", label: "is not empty" }
        ]
      
      case 'select':
      case 'radio':
        return [
          { value: "equals", label: "equals" },
          { value: "not_equals", label: "not equals" },
          { value: "is_empty", label: "is empty" },
          { value: "is_not_empty", label: "is not empty" }
        ]
      
      case 'email':
        return [
          { value: "equals", label: "equals" },
          { value: "not_equals", label: "not equals" },
          { value: "contains", label: "contains" },
          { value: "not_contains", label: "not contains" },
          { value: "starts_with", label: "starts with" },
          { value: "ends_with", label: "ends with" },
          { value: "is_empty", label: "is empty" },
          { value: "is_not_empty", label: "is not empty" }
        ]
      
      default: // text, textarea, etc.
        return [
          { value: "equals", label: "equals" },
          { value: "not_equals", label: "not equals" },
          { value: "contains", label: "contains" },
          { value: "not_contains", label: "not contains" },
          { value: "starts_with", label: "starts with" },
          { value: "ends_with", label: "ends with" },
          { value: "is_empty", label: "is empty" },
          { value: "is_not_empty", label: "is not empty" }
        ]
    }
  }

  // Get connected request nodes for display
  const getConnectedRequestNodes = () => {
    const connectedRequests = []
    const incomingEdges = edges.filter(edge => edge.target === id)
    
    incomingEdges.forEach(edge => {
      const sourceNode = nodes.find(node => node.id === edge.source)
      if (sourceNode && isRequestLikeNode(sourceNode)) {
        connectedRequests.push({
          id: sourceNode.id,
          name: sourceNode.data.name || 'HTTP Request',
          url: sourceNode.data.url || ''
        })
      }
    })
    
    return connectedRequests
  }

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  // Keep wheel scrolling contained within this node while hovered.
  useEffect(() => {
    const handleWheel = (e) => {
      const ifElseNode = e.target.closest(".if-else-node-outline")
      if (!ifElseNode) return

      // Preserve browser/page zoom and global horizontal scroll shortcuts.
      if (e.ctrlKey || e.shiftKey) return

      e.stopPropagation()
    }

    document.addEventListener("wheel", handleWheel, { passive: false, capture: true })

    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true })
    }
  }, [id, expanded])

  // Scroll expanded condition header to top of node content (within 446px scroll area)
  const scrollConditionHeaderToTop = useCallback((conditionId) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollContainer = nodeContentRef.current
        const conditionItem = conditionRefs.current[conditionId]
        const headerEl = conditionItem?.querySelector(".condition-header")
        if (!scrollContainer || !headerEl) return

        const containerRect = scrollContainer.getBoundingClientRect()
        const headerRect = headerEl.getBoundingClientRect()
        const headerOffsetInContainer = headerRect.top - containerRect.top + scrollContainer.scrollTop
        const maxScroll = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight)
        const targetScroll = Math.max(0, Math.min(headerOffsetInContainer, maxScroll))

        if (Math.abs(scrollContainer.scrollTop - targetScroll) < 1) return

        scrollContainer.scrollTo({ top: targetScroll, behavior: "smooth" })
      })
    })
  }, [])

  // Toggle condition expansion (accordion: only one condition expanded at a time)
  const toggleConditionExpansion = (conditionId) => {
    let shouldScroll = false

    setExpandedConditions((prev) => {
      if (prev.has(conditionId)) {
        const next = new Set(prev)
        next.delete(conditionId)
        return next
      }

      shouldScroll = true
      return new Set([conditionId])
    })

    if (shouldScroll) {
      scrollConditionHeaderToTop(conditionId)
    }
  }

  // Operator options
  const operators = [
    { value: "equals", label: "equals" },
    { value: "not_equals", label: "not equals" },
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "not contains" },
    { value: "in", label: "in" },
    { value: "greater_than", label: "greater than" },
    { value: "less_than", label: "less than" },
    { value: "greater_than_or_equal", label: "≥" },
    { value: "less_than_or_equal", label: "≤" },
    { value: "starts_with", label: "starts with" },
    { value: "ends_with", label: "ends with" },
    { value: "is_empty", label: "is empty" },
    { value: "is_not_empty", label: "is not empty" }
  ]

  // Comparison type options
  const comparisonTypes = [
    { value: "response_field", label: "Response Field" },
    { value: "response_json_key", label: "Response JSON key" },
    // { value: "status_code", label: "Status Code" },
    // { value: "custom_expression", label: "Custom Expression" }
  ]

  const responseJsonInValueOptions = [
    {
      value: "suppression_active:workflow_task_creation",
      label: "Active suppression: workflow_task_creation (workflow/global)",
    },
    {
      value: "suppression_active:any",
      label: "Active suppression: any type (workflow/global)",
    },
  ]

  // Common response fields for different node types
  const responseFields = [
    // Standard response fields
    { value: "status", label: "Status" },
    { value: "statusCode", label: "Status Code" },
    { value: "body", label: "Response Body" },
    { value: "headers", label: "Headers" },
    { value: "error", label: "Error" },
    { value: "success", label: "Success" },
    { value: "data", label: "Data" },
    { value: "message", label: "Message" }
  ]

  // Get form fields separately for better organization
  const formFields = getAvailableFormFields()
  
  // Create a combined list with form fields prominently displayed
  const allAvailableFields = [
    ...responseFields,
    // Add separator if we have form fields
    ...(formFields.length > 0 ? [{ value: "", label: "--- Form Fields ---", disabled: true }] : []),
    // Add form fields with clear labeling and icons
    ...formFields.map(field => {
      const fieldTypeIcons = {
        'text': '📝',
        'email': '📧',
        'number': '🔢',
        'textarea': '📄',
        'select': '📋',
        'checkbox': '☑️',
        'radio': '🔘',
        'date': '📅'
      }
      
      return {
        value: field.value,
        label: formatFormFieldSelectOptionLabel(field, {
          icon: fieldTypeIcons[field.fieldType] || '📄',
        }),
        fieldType: field.fieldType,
      }
    })
  ]

  const syncLocalState = useCallback((updatedData) => {
    conditionDataRef.current = updatedData
    setConditionData(updatedData)
    if (currentWorkflow) {
      updateNode(currentWorkflow.id, id, { data: updatedData })
    }
  }, [currentWorkflow, id, updateNode])

  const saveToBuilder = useCallback(() => {
    if (!data.onNodeDataChange) return
    data.onNodeDataChange(id, buildNodePersistPayload(conditionDataRef.current, { expanded, data }))
  }, [data, expanded, id])

  const handleBlur = () => {
    saveToBuilder()
  }

  const shouldAutoLabelCondition = (condition) => {
    if (condition.nameCustomized === true) return false
    if (condition.nameCustomized === false) return true
    // Legacy workflows without nameCustomized: only auto-label while still on a default name
    return isAnyDefaultConditionName(condition.description)
  }

  const getOperatorLabel = (operatorValue) => {
    const match = operators.find((op) => op.value === operatorValue)
    return match?.label || operatorValue
  }

  const generateConditionLabel = (condition) => {
    if (condition.comparisonType === "custom_expression") {
      const expression = (condition.expression || "").trim()
      return truncateLabel(expression || "Custom expression")
    }

    if (condition.comparisonType === "response_json_key") {
      const key = (condition.responseBodyKey && String(condition.responseBodyKey).trim()) || "key"
      const op = condition.operator || "equals"

      if (op === "in") {
        const selected = responseJsonInValueOptions.find((option) => option.value === condition.value)
        const valueLabel = selected?.label || condition.value || "..."
        return truncateLabel(`${key} in ${valueLabel}`)
      }

      if (op === "is_empty" || op === "is_not_empty") {
        return truncateLabel(`${key} ${getOperatorLabel(op)}`)
      }

      const value = condition.value ?? ""
      return truncateLabel(`${key} ${getOperatorLabel(op)} ${value}`)
    }

    const field = condition.field
    const operator = condition.operator
    const value = condition.value
    const formFields = getAvailableFormFields()
    const fieldInfo = formFields.find((f) => String(f.value) === String(field))
    const responseFieldInfo = responseFields.find((f) => f.value === field)
    const fieldLabel = fieldInfo
      ? (fieldInfo.label || fieldInfo.name || field)
      : (responseFieldInfo?.label || field)

    const availableValues = getAvailableValuesForField(field)
    const selectedValueOption = availableValues.find(
      (option) => String(option.value) === String(value)
    )
    const valueLabel = selectedValueOption?.label || value

    if (operator === "is_empty" || operator === "is_not_empty") {
      return truncateLabel(`${fieldLabel} ${getOperatorLabel(operator)}`)
    }

    if (!valueLabel) {
      return truncateLabel(`${fieldLabel} ${getOperatorLabel(operator)}`)
    }

    return truncateLabel(`${fieldLabel} ${getOperatorLabel(operator)} ${valueLabel}`)
  }

  // Update condition
  const updateCondition = (conditionId, updates) => {
    // Process updates to ensure string values are properly handled
    const processedUpdates = { ...updates }
    
    // Ensure string fields are never null
    if (processedUpdates.description !== undefined) {
      processedUpdates.description = ensureString(processedUpdates.description)
    }
    if (processedUpdates.value !== undefined) {
      processedUpdates.value = ensureString(processedUpdates.value)
    }
    if (processedUpdates.field !== undefined) {
      processedUpdates.field = ensureString(processedUpdates.field)
    }
    if (processedUpdates.expression !== undefined) {
      processedUpdates.expression = ensureString(processedUpdates.expression)
    }
    if (processedUpdates.responseBodyKey !== undefined) {
      processedUpdates.responseBodyKey = ensureString(processedUpdates.responseBodyKey)
    }
    // If field is being changed, validate and potentially reset operator and value
    if (processedUpdates.field !== undefined) {
      const currentCondition = conditionDataRef.current.conditions.find(c => c.id === conditionId)
      const newFieldOperators = getOperatorsForField(processedUpdates.field)
      const currentOperator = currentCondition?.operator
      
      // Check if current operator is valid for the new field
      const isValidOperator = newFieldOperators.some(op => op.value === currentOperator)
      
      if (!isValidOperator && currentOperator) {
        // Reset to first valid operator
        processedUpdates.operator = newFieldOperators[0]?.value || "equals"
        // Clear value since it might not be appropriate for the new field type
        processedUpdates.value = ""
      }
    }

    const affectsLabel = LABEL_PARAM_KEYS.some(
      (key) => processedUpdates[key] !== undefined
    )
    
    const updatedData = {
      ...conditionDataRef.current,
      conditions: conditionDataRef.current.conditions.map((condition) => {
        if (condition.id !== conditionId) return condition

        const merged = { ...condition, ...processedUpdates }

        if (affectsLabel && shouldAutoLabelCondition(merged)) {
          merged.description = generateConditionLabel(merged)
          merged.nameCustomized = false
        }

        return merged
      }),
    }

    syncLocalState(updatedData)
  }

  // Handle condition data changes
  const handleChange = (field, value) => {
    syncLocalState({ ...conditionDataRef.current, [field]: value })
  }

  const addCondition = () => {
    const newConditionIndex = conditionData.conditions.length
    const newCondition = {
      id: `condition-${Date.now()}`,
      type: newConditionIndex === 0 ? "if" : "else if",
      description: `Condition ${newConditionIndex + 1}`,
      nameCustomized: false,
      operator: "equals",
      value: "",
      field: "status",
      comparisonType: "response_field",
      expression: "",
      responseBodyKey: "",
    }

    const updatedData = {
      ...conditionDataRef.current,
      conditions: [...conditionDataRef.current.conditions, newCondition],
    }

    syncLocalState(updatedData)
    saveToBuilder()
  }

  const removeCondition = async (conditionId) => {
    const currentWorkflowData = workflows.find(w => w.id === currentWorkflow?.id)
    const workflowEdges = currentWorkflowData?.edges || []

    const conditionHandleId = `output-${conditionId}`
    const associatedEdge = workflowEdges.find(edge =>
      edge.source === id && edge.sourceHandle === conditionHandleId
    )

    if (associatedEdge) {
      if (window.workflowContext && window.workflowContext.deleteEdge) {
        try {
          await window.workflowContext.deleteEdge(window.workflowContext.workflowId, associatedEdge.id)
        } catch (error) {
          console.error('IfElseNode: failed to delete edge', error)
        }
      }
    }

    const remainingConditions = conditionDataRef.current.conditions.filter(
      (condition) => condition.id !== conditionId
    )

    const updatedConditions = remainingConditions.map((condition, index) => {
      const newType = index === 0 ? "if" : "else if"
      let newDescription = condition.description

      if (!condition.nameCustomized && isAnyDefaultConditionName(condition.description)) {
        newDescription = `Condition ${index + 1}`
      }

      return {
        ...condition,
        type: newType,
        description: newDescription,
      }
    })

    const updatedData = {
      ...conditionDataRef.current,
      conditions: updatedConditions,
    }

    syncLocalState(updatedData)
    saveToBuilder()
  }

  // Generate expression preview
  const generateExpression = (condition) => {
    if (condition.comparisonType === "custom_expression") {
      return condition.expression
    }

    if (condition.comparisonType === "response_json_key") {
      const key = (condition.responseBodyKey && String(condition.responseBodyKey).trim()) || "(key)"
      const op = condition.operator || "equals"
      if (op === "in") {
        const selected = responseJsonInValueOptions.find((o) => o.value === condition.value)
        return `If ${key} is in ${selected?.label || condition.value || "(set)"}`
      }
      const raw = condition.value ?? ""
      const quoted = JSON.stringify(String(raw))
      const readableOperatorMap = {
        equals: "equals",
        not_equals: "does not equal",
        contains: "contains",
        not_contains: "does not contain",
        greater_than: "is greater than",
        less_than: "is less than",
        greater_than_or_equal: "is greater than or equal to",
        less_than_or_equal: "is less than or equal to",
        starts_with: "starts with",
        ends_with: "ends with",
        is_empty: "is empty",
        is_not_empty: "is not empty",
      }
      const readableOp = readableOperatorMap[op] || op
      if (op === "is_empty" || op === "is_not_empty") {
        return `If ${key} ${readableOp}`
      }
      return `If ${key} ${readableOp} ${quoted}`
    }
    
    const field = condition.field
    const operator = condition.operator
    const value = condition.value
    
    // Get field type to determine if we need special handling
    const formFields = getAvailableFormFields()
    const fieldInfo = formFields.find(f => String(f.value) === String(field))
    const responseFieldInfo = responseFields.find((f) => f.value === field)
    const formFieldLabel = fieldInfo
      ? (fieldInfo.label || `${fieldInfo.source || "Form"}: ${fieldInfo.name}`)
      : null
    const fieldLabel = formFieldLabel || responseFieldInfo?.label || field

    const availableValues = getAvailableValuesForField(field)
    const selectedValueOption = availableValues.find((option) => String(option.value) === String(value))
    const valueLabel = selectedValueOption?.label || value
    const isNumberField = fieldInfo?.fieldType === 'number'
    const isDateField = fieldInfo?.fieldType === 'date'
    const isCheckboxField = fieldInfo?.fieldType === 'checkbox'
    
    // Helper function to format value based on field type
    const formatValue = (val) => {
      if (isNumberField) {
        return isNaN(val) ? `"${val}"` : val
      }
      if (isDateField) {
        return `"${val}"`
      }
      if (isCheckboxField) {
        // Handle boolean values for checkboxes
        if (val === 'true' || val === '1') return 'true'
        if (val === 'false' || val === '0') return 'false'
        return `"${val}"`
      }
      return `"${val}"`
    }
    
    switch (operator) {
      case "equals":
        return `${fieldLabel} === ${formatValue(valueLabel)}`
      case "not_equals":
        return `${fieldLabel} !== ${formatValue(valueLabel)}`
      case "contains":
        return `${fieldLabel}.includes(${formatValue(valueLabel)})`
      case "not_contains":
        return `!${fieldLabel}.includes(${formatValue(valueLabel)})`
      case "greater_than":
        return `${fieldLabel} > ${formatValue(valueLabel)}`
      case "less_than":
        return `${fieldLabel} < ${formatValue(valueLabel)}`
      case "greater_than_or_equal":
        return `${fieldLabel} >= ${formatValue(valueLabel)}`
      case "less_than_or_equal":
        return `${fieldLabel} <= ${formatValue(valueLabel)}`
      case "starts_with":
        return `${fieldLabel}.startsWith(${formatValue(valueLabel)})`
      case "ends_with":
        return `${fieldLabel}.endsWith(${formatValue(valueLabel)})`
      case "is_empty":
        if (isCheckboxField) {
          return `!${fieldLabel} || ${fieldLabel} === false || ${fieldLabel} === "false" || ${fieldLabel} === "0"`
        }
        return `!${fieldLabel} || ${fieldLabel}.length === 0 || ${fieldLabel} === ""`
      case "is_not_empty":
        if (isCheckboxField) {
          return `${fieldLabel} === true || ${fieldLabel} === "true" || ${fieldLabel} === "1"`
        }
        return `${fieldLabel} && ${fieldLabel}.length > 0 && ${fieldLabel} !== ""`
      default:
        return `${fieldLabel} === ${formatValue(valueLabel)}`
    }
  }

  // Get condition type label
  const getConditionType = (index) => {
    if (index === 0) return "if"
    if (index === conditionData.conditions.length - 1 && conditionData.conditions.length > 1) return "else"
    return "else if"
  }

  // Validate condition
  const validateCondition = (condition) => {
    const errors = []

    if (condition.comparisonType === "custom_expression") {
      if (!condition.expression) {
        errors.push("Expression is required")
      }
      return errors
    }

    if (condition.comparisonType === "response_json_key") {
      if (!condition.responseBodyKey || !String(condition.responseBodyKey).trim()) {
        errors.push("JSON key is required")
      }
      if (condition.operator === "in" && !condition.value) {
        errors.push("Value source is required for IN operator")
      }
    } else if (!condition.field) {
      errors.push("Field is required")
    }

    if (!condition.operator) {
      errors.push("Operator is required")
    }

    const operatorsThatNeedValue = [
      "equals", "not_equals", "contains", "not_contains",
      "greater_than", "less_than", "greater_than_or_equal",
      "less_than_or_equal", "starts_with", "ends_with", "in",
    ]

    if (operatorsThatNeedValue.includes(condition.operator) && !condition.value) {
      errors.push("Value is required for this operator")
    }

    return errors
  }

  // Get validation status for a condition
  const getConditionValidationStatus = (condition) => {
    const errors = validateCondition(condition)
    return {
      isValid: errors.length === 0,
      errors
    }
  }
  
  return (
    <div
      className={`trigger-node-container if-else-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? "if-else-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Node Header */}
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <GitBranch size={16} className="trigger-icon" />
          <span>{conditionData.name && conditionData.name.trim() !== "" ? conditionData.name : "If/Else"}</span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons 
            nodeId={id}
            nodeType="ifElseNode"
            nodeName={conditionData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <button 
            className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
            onClick={async (e) => {
              e.stopPropagation()
              if (confirm('Are you sure you want to delete this node?')) {
                try {
                  if (!resolvedWorkflowId || !stepDatabaseId) {
                    throw new Error('Unable to delete node: missing workflow or step database ID')
                  }
                  router.delete(`/workflows/${resolvedWorkflowId}/steps/${stepDatabaseId}`, {
                    preserveState: true,
                    preserveScroll: true,
                    only: [],
                    onSuccess: () => {
                      if (typeof data.onDeleteNode === "function") {
                        data.onDeleteNode(id)
                      }
                    },
                    onError: (errors) => {
                      console.error('Failed to delete if/else node via Inertia:', errors)
                      alert('Failed to delete node. Please try again.')
                    },
                  })
                  if (typeof data.onDeleteNode !== "function" && deleteNode && currentWorkflow) {
                    await deleteNode(currentWorkflow.id, id, csrf_token)
                  } else {
                    // no-op: Inertia success + onDeleteNode handles visual removal
                  }
                } catch (error) {
                  console.error('Failed to delete node:', error)
                  alert('Failed to delete node. Please try again.')
                }
              }
            }}
            title="Delete node"
            style={{ color: '#ef4444' }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Node Content */}
      {expanded && (
        <div
          ref={nodeContentRef}
          className="node-content dark:bg-card dark:text-white dark:border-border"
        >
          {/* Tabs */}
          {/* <div className="if-else-tabs">
            <button
              className={`tab-button ${activeTab === "conditions" ? "active" : ""}`}
              onClick={() => setActiveTab("conditions")}
            >
              Conditions
            </button>
            <button
              className={`tab-button ${activeTab === "settings" ? "active" : ""}`}
              onClick={() => setActiveTab("settings")}
            >
              Settings
            </button>
          </div> */}

          {/* Conditions Tab */}
          {/* {activeTab === "conditions" && ( */}
            <div className="conditions-tab-content dark:bg-card dark:text-white dark:border-border">
              <div className="node-field dark:text-white">
                <label className="dark:text-white">Node Name</label>
                <VariableReference
                  value={conditionData.name}
                  onChange={(value) => handleChange("name", value)}
                  onBlur={() => handleBlur("name")}
                  availableVariables={getAvailableVariables()}
                  placeholder="Enter condition name or use variables..."
                  className="w-full"
                />
                {/* Show connected request nodes */}
                {(() => {
                  const connectedRequests = getConnectedRequestNodes()
                  if (connectedRequests.length > 0) {
                    return (
                      <div style={{ 
                        marginTop: '4px', 
                        fontSize: '11px', 
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <ExternalLink size={10} />
                        <span>Connected to: {connectedRequests.map(req => req.name).join(', ')}</span>
                      </div>
                    )
                  }
                  return null
                })()}
                
                {/* Show available form fields indicator */}
                {(() => {
                  if (formFields.length > 0) {
                    return (
                      <div style={{ 
                        marginTop: '4px', 
                        fontSize: '11px', 
                        color: '#059669',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <FileText size={10} />
                        <span>Available form fields: {formFields.length} field{formFields.length !== 1 ? 's' : ''}</span>
                      </div>
                    )
                  }
                  return null
                })()}
              </div>

              <div className="node-field dark:text-white">
                <label className="dark:text-white">Description</label>
                <VariableReference
                  value={conditionData.description}
                  onChange={(value) => handleChange("description", value)}
                  onBlur={() => handleBlur("description")}
                  availableVariables={getAvailableVariables()}
                  placeholder="Describe this condition..."
                  className="w-full"
                />
              </div>

              <div className="conditions-section">
                <div className="section-header">
                  <label className="dark:text-white">Conditions</label>
                  <button 
                    className="add-condition-button dark:hover:bg-card dark:hover:text-white"
                    onClick={addCondition}
                  >
                    <PlusIcon size={14} />
                    Add Condition
                  </button>
                </div>
                
                {/* Overall validation status */}
                {(() => {
                  const allValidations = conditionData.conditions.map(condition => getConditionValidationStatus(condition))
                  const hasErrors = allValidations.some(v => !v.isValid)
                  const totalErrors = allValidations.reduce((sum, v) => sum + v.errors.length, 0)
                  
                  if (hasErrors) {
                    return (
                      <div style={{
                        marginBottom: '12px',
                        padding: '8px 12px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#dc2626',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <AlertTriangle size={14} />
                        <span>This node has {totalErrors} validation error{totalErrors !== 1 ? 's' : ''} that need to be fixed.</span>
                      </div>
                    )
                  }
                  return null
                })()}

                <div className="conditions-list-scrollable">
                  <TooltipProvider delayDuration={300}>
                  <div className="conditions-list">
                    {conditionData.conditions.map((condition, index) => {
                    const isExpanded = expandedConditions.has(condition.id)
                    const conditionTitle = condition.description && condition.description.trim() !== ""
                      ? condition.description
                      : `Condition ${index + 1}`
                    return (
                      <div
                        key={condition.id}
                        className="condition-item dark:bg-card dark:text-white dark:border-border"
                        ref={(el) => {
                          if (el) {
                            conditionRefs.current[condition.id] = el
                          } else {
                            delete conditionRefs.current[condition.id]
                          }
                        }}
                      >
                        <div 
                          className="condition-header dark:bg-[#1f2937] dark:text-white dark:border-border"
                          onClick={() => toggleConditionExpansion(condition.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="condition-header-left">
                            <span className="condition-number dark:text-white">#{index + 1}</span>
                            <span className="condition-type">{getConditionType(index)}</span>
                            <ConditionNameLabel title={conditionTitle} />
                            {(() => {
                              const validation = getConditionValidationStatus(condition)
                              if (!validation.isValid) {
                                return (
                                  <span style={{ 
                                    color: '#ef4444', 
                                    fontSize: '11px',
                                    marginLeft: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <AlertTriangle size={10} />
                                    {validation.errors.length} error{validation.errors.length !== 1 ? 's' : ''}
                                  </span>
                                )
                              }
                              return null
                            })()}
                          </div>
                          <div className="condition-header-right">
                            {conditionData.conditions.length > 1 && (
                              <button 
                                className="remove-condition-button dark:hover:bg-[#1f2937] dark:hover:text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  removeCondition(condition.id)
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="condition-content dark:bg-[#1f2937] dark:text-white dark:border-border">
                            <div className="condition-section dark:bg-[#1f2937] dark:text-white dark:border-border">
                              <div className="condition-row condition-name-row dark:bg-[#1f2937] dark:text-white dark:border-border">
                                <div className="condition-input condition-name-input dark:bg-[#1f2937] dark:text-white dark:border-border">
                                  <label className="dark:text-white">Name</label>
                                  <input
                                    type="text"
                                    value={condition.description}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const value = e.target.value
                                      updateCondition(condition.id, {
                                        description: value,
                                        nameCustomized: !isAnyDefaultConditionName(value),
                                      })
                                    }}
                                    onBlur={() => handleBlur()}
                                    placeholder={`Condition ${index + 1}`}
                                    className="dark:bg-[#1f2937] dark:text-white dark:border-white"
                                  />
                                </div>
                              </div>
                              <div className="condition-row dark:bg-[#1f2937] dark:text-white dark:border-border">
                                <div className="condition-input dark:bg-[#1f2937] dark:text-white dark:border-border">
                                  <label className="dark:text-white">Compare:</label>
                                  <select
                                    value={condition.comparisonType}
                                    onChange={(e) => {
                                      const next = e.target.value
                                      const payload = { comparisonType: next }
                                      if (next === "response_json_key") {
                                        payload.responseBodyKey = condition.responseBodyKey || ""
                                      }
                                      updateCondition(condition.id, payload)
                                    }}
                                    onBlur={() => handleBlur("condition")}
                                    className="dark:bg-[#1f2937] dark:text-white dark:border-white"
                                  >
                                    {comparisonTypes.map(type => (
                                      <option key={type.value} value={type.value} className="dark:bg-[#1f2937] dark:text-white dark:border-border">
                                        {type.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {condition.comparisonType !== "custom_expression" && (
                                <>
                                  {condition.comparisonType === "response_field" && (
                                    <div className="condition-row flex-col">
                                      <div className="condition-input dark:bg-[#1f2937] dark:text-white dark:border-border">
                                        <label className="dark:text-white">Field:</label>
                                        <select
                                          value={condition.field}
                                          onChange={(e) => updateCondition(condition.id, { field: e.target.value })}
                                          onBlur={() => handleBlur("condition")}
                                          className="dark:bg-[#1f2937] dark:text-white dark:border-white"
                                        >
                                          {allAvailableFields.map(field => (
                                            <option key={field.value} value={field.value} disabled={field.disabled} className="dark:bg-[#1f2937] dark:text-white dark:border-border">
                                              {field.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      
                                      {(() => {
                                        const formFieldsLocal = getAvailableFormFields()
                                        const selectedField = formFieldsLocal.find(f => f.value === condition.field)
                                        
                                        if (selectedField) {
                                          const fieldTypeLabels = {
                                            'text': 'Text Input',
                                            'email': 'Email',
                                            'number': 'Number',
                                            'textarea': 'Text Area',
                                            'select': 'Dropdown',
                                            'checkbox': 'Checkbox',
                                            'radio': 'Radio Buttons',
                                            'date': 'Date'
                                          }
                                          
                                          return (
                                            <div className="field-type-info dark:bg-[#1f2937] dark:text-white dark:border-border" style={{
                                              marginTop: '4px',
                                              fontSize: '11px',
                                              color: '#059669',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px'
                                            }}>
                                              <span>📋 {selectedField.label || "Field"} · {`{{${selectedField.name}}}`}</span>
                                              <span>· Field type: {fieldTypeLabels[selectedField.fieldType] || selectedField.fieldType}</span>
                                              {selectedField.options && selectedField.options.length > 0 && (
                                                <span>• {selectedField.options.length} option{selectedField.options.length !== 1 ? 's' : ''} available</span>
                                              )}
                                            </div>
                                          )
                                        }
                                        return null
                                      })()}
                                      
                                      {formFields.length > 0 && (
                                        <div className="form-fields-help dark:bg-[#1f2937] text-[#666] dark:text-white dark:border-border">
                                          <small style={{ fontStyle: 'italic' }}>
                                            Form fields from upstream Form nodes and post-completion forms on upstream Task nodes are available above.
                                            Use canonical tokens such as {'{{myform.select}}'} for stored values, or {'{{myform.select_label}}'} for dropdown display text. Field-name label tokens are no longer supported at runtime.
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {condition.comparisonType === "response_json_key" && (() => {
                                    const jsonKeys = getUpstreamRequestResponseKeys()
                                    const keyVal = condition.responseBodyKey || ""
                                    const selectValue = jsonKeys.includes(keyVal) ? keyVal : ""
                                    return (
                                      <div className="condition-row flex-col">
                                        <div className="condition-input dark:bg-[#1f2937] dark:text-white dark:border-border">
                                          <label className="dark:text-white">JSON key:</label>
                                          <select
                                            value={selectValue}
                                            onChange={(e) => updateCondition(condition.id, { responseBodyKey: e.target.value })}
                                            onBlur={() => handleBlur("condition")}
                                            className="dark:bg-[#1f2937] dark:text-white dark:border-white mb-1"
                                          >
                                            <option value="">Select a key from latest request response...</option>
                                            {jsonKeys.map((keyName) => (
                                              <option key={keyName} value={keyName}>{keyName}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div className="form-fields-help dark:bg-[#1f2937] text-[#666] dark:text-white dark:border-border">
                                          <small style={{ fontStyle: 'italic' }}>
                                            True if <strong>any record</strong> in the response matches (for arrays of objects, each item is one record).
                                            Uses the workflow instance&apos;s latest HTTP response body (<code className="text-gray-400">response_data</code>).
                                            Run <strong>Test</strong> on upstream Request nodes to refresh suggested keys. If several requests run before this branch, only the most recent response is used.
                                          </small>
                                        </div>
                                      </div>
                                    )
                                  })()}

                                  <div className="condition-row flex-col">
                                    <div className="condition-input dark:bg-[#1f2937] dark:text-white dark:border-border">
                                      <label className="dark:text-white">Operator:</label>
                                      <select
                                        value={condition.operator}
                                        onChange={(e) => updateCondition(condition.id, { operator: e.target.value })}
                                        onBlur={() => handleBlur("condition")}
                                        className="dark:bg-[#1f2937] dark:text-white dark:border-white"
                                      >
                                        {(condition.comparisonType === "response_json_key"
                                          ? operators
                                          : getOperatorsForField(condition.field)
                                        ).map(op => (
                                          <option key={op.value} value={op.value} className="dark:bg-[#1f2937] dark:text-white dark:border-border">
                                            {op.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>

                                  <div className="condition-row dark:border-none">
                                    <div className="condition-input dark:bg-[#1f2937] dark:text-white dark:border-border">
                                      <label className="dark:text-white">Value:</label>
                                      {(() => {
                                        if (condition.comparisonType === "response_json_key") {
                                          if (condition.operator === "in") {
                                            return (
                                              <select
                                                value={condition.value}
                                                onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                                                onBlur={() => handleBlur("condition")}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-[#1f2937] dark:text-white dark:border-none"
                                              >
                                                <option value="">Select a value set...</option>
                                                {responseJsonInValueOptions.map((option) => (
                                                  <option key={option.value} value={option.value}>
                                                    {option.label}
                                                  </option>
                                                ))}
                                              </select>
                                            )
                                          }
                                          return (
                                            <VariableReference
                                              value={condition.value}
                                              onChange={(value) => updateCondition(condition.id, { value })}
                                              onBlur={() => handleBlur("condition")}
                                              availableVariables={getAvailableVariables()}
                                              placeholder="Enter value to compare..."
                                              className="dark:bg-[#1f2937] dark:text-white"
                                            />
                                          )
                                        }
                                        const availableValues = getAvailableValuesForField(condition.field)
                                        const isFormField = getAvailableFormFields().some(f => f.value === condition.field)
                                        
                                        if (availableValues.length > 0 && isFormField) {
                                          return (
                                            <select
                                              value={condition.value}
                                              onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                                              onBlur={() => handleBlur("condition")}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-[#1f2937] dark:text-white dark:border-none"
                                            >
                                              <option value="">Select a value...</option>
                                              {availableValues.map(option => (
                                                <option key={option.value} value={option.value}>
                                                  {option.label}
                                                </option>
                                              ))}
                                            </select>
                                          )
                                        }
                                        return (
                                          <VariableReference
                                            value={condition.value}
                                            onChange={(value) => updateCondition(condition.id, { value })}
                                            onBlur={() => handleBlur("condition")}
                                            availableVariables={getAvailableVariables()}
                                            placeholder="Enter value to compare..."
                                            className="dark:bg-[#1f2937] dark:text-white"
                                          />
                                        )
                                      })()}
                                    </div>
                                  </div>
                                </>
                              )}

                              {condition.comparisonType === "custom_expression" && (
                                <div className="condition-row dark:border-none">
                                  <div className="condition-input">
                                    <label className="dark:text-white">Expression:</label>
                                    <VariableReference
                                      value={condition.expression}
                                      onChange={(value) => updateCondition(condition.id, { expression: value })}
                                      onBlur={() => handleBlur("condition")}
                                      availableVariables={getAvailableVariables()}
                                      placeholder="Enter custom expression..."
                                      className="dark:bg-[#1f2937] dark:text-white dark:border-white"
                                    />
                                  </div>
                                </div>
                              )}

                              <div className="condition-preview dark:bg-card dark:text-white dark:border-white dark:border-l-[#8b5cf6]">
                                <strong>Preview:</strong> {generateExpression(condition)}
                              </div>
                              
                              {/* Show validation errors */}
                              {(() => {
                                const validation = getConditionValidationStatus(condition)
                                if (!validation.isValid) {
                                  return (
                                    <div className="condition-errors dark:bg-[#1f2937] dark:text-white dark:border-white" style={{
                                      marginTop: '8px',
                                      padding: '8px 12px',
                                      backgroundColor: '#fef2f2',
                                      border: '1px solid #fecaca',
                                      borderRadius: '6px',
                                      fontSize: '12px'
                                    }}>
                                      <div style={{ fontWeight: '500', marginBottom: '4px' }} className="dark:text-red-600">
                                        Validation Errors:
                                      </div>
                                      <ul style={{ margin: 0, paddingLeft: '16px' }} className="dark:text-red-600 dark:border-white">
                                        {validation.errors.map((error, errorIndex) => (
                                          <li key={errorIndex}>{error}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )
                                }
                                return null
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                  </div>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          {/* )} */}

          {/* Settings Tab */}
          {/* {activeTab === "settings" && (
            
          )} */}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        id="input"
      />

      {/* Output handles for each condition */}
      <div className="output-handles-container dark:bg-card dark:text-white dark:border-border">
        {conditionData.conditions.length > 0 ? (
          conditionData.conditions.map((condition, index) => {
            const totalConditions = conditionData.conditions.length
            let position
            
            if (totalConditions === 1) {
              // Single condition: center at 175px
              position = 175
            } else if (totalConditions === 2) {
              // Two conditions: 25px and 325px from edges
              position = index === 0 ? 25 : 325
            } else {
              // Three or more conditions: first at 25px, last at 325px, others evenly spaced
              if (index === 0) {
                position = 25 // First handle
              } else if (index === totalConditions - 1) {
                position = 325 // Last handle
              } else {
                // Middle handles: evenly spaced between 25 and 325
                const availableSpace = 300 // 325 - 25
                const spacing = availableSpace / (totalConditions - 1)
                position = 25 + (index * spacing)
              }
            }
            return (
              <div key={condition.id} className="output-handle-wrapper">
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id={`output-${condition.id}`}
                  // className="connection-handle output-handle"
                  style={{
                    left: `${position}px`,
                    transform: "translateX(-50%)"
                  }}
                />
                <div 
                  className="handle-label dark:text-white"
                  style={{
                    left: `${position}px`,
                    transform: "translateX(-50%)"
                  }}
                >
                  {`#${index + 1}`}
                </div>
              </div>
            )
          })
        ) : (
          // Default output handle when no conditions exist
          <div className="output-handle-wrapper">
            <Handle
              type="source"
              position={Position.Bottom}
              id="output-default"
              className="connection-handle output-handle"
              style={{
                left: '175px',
                transform: "translateX(-50%)"
              }}
            />
            <div 
              className="handle-label"
              style={{
                left: '175px',
                transform: "translateX(-50%)"
              }}
            >
              Default
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default IfElseNode 